import { useCallback, useEffect, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import {
    brancheVorschlagen,
    type BranchenvorschlagAntwort,
} from "../../services/firmendatenService";

/**
 * Die Einordnung in den Branchenkatalog — vorgeschlagen aus dem
 * Unternehmensgegenstand.
 *
 * Damit schließt sich die Kette: Der Gesellschaftsvertrag (oder der
 * chronologische Abdruck) liefert den Gegenstand, der Gegenstand steht in der
 * Tätigkeitsbeschreibung, und daraus wird die Branche vorgeschlagen.
 *
 * **Warum jeder Vorschlag seine Wörter mitbringt.** An der Klassifikation
 * hängen Risikofragen und Vorbelegungen — eine falsche Einordnung wirkt weiter,
 * und zwar unsichtbar. Wer sieht, dass ein Vorschlag auf „sanitär, heizungs,
 * installation" beruht, kann ihn beurteilen; wer nur eine Zeile mit einer Zahl
 * sieht, übernimmt sie blind oder ignoriert sie blind.
 */
const useStyles = makeStyles({
    kopfzeile: { fontSize: "13px", color: "#6b5a66", marginBottom: "12px", lineHeight: 1.5 },
    gegenstand: {
        ...shorthands.padding("10px", "12px"),
        ...shorthands.borderRadius("9px"),
        backgroundColor: "#f8f4f8",
        marginBottom: "14px",
        fontSize: "13px",
        lineHeight: 1.5,
        color: "#3d2d39",
    },
    beschriftung: { fontSize: "11.5px", fontWeight: 700, color: "#6b5a66", display: "block" },
    zeile: {
        display: "flex",
        alignItems: "flex-start",
        ...shorthands.gap("10px"),
        ...shorthands.padding("11px", "4px"),
        ...shorthands.borderBottom("1px", "solid", "#ece6ec"),
        cursor: "pointer",
        ":hover": { backgroundColor: "#faf6f9" },
    },
    inhalt: { flexGrow: 1, minWidth: 0 },
    pfad: {
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "14px",
        fontWeight: 700,
        color: "#241522",
        lineHeight: 1.3,
    },
    woerter: { display: "block", fontSize: "12.5px", color: "#6b5a66", marginTop: "3px" },
    punkte: {
        flexShrink: 0,
        fontSize: "11.5px",
        fontWeight: 700,
        color: "#7d2169",
        fontVariantNumeric: "tabular-nums",
    },
    hinweis: {
        fontSize: "12.5px",
        color: "#7a5a18",
        backgroundColor: "#fdf6e3",
        ...shorthands.padding("8px", "10px"),
        ...shorthands.borderRadius("8px"),
        marginTop: "10px",
        lineHeight: 1.45,
    },
    fehler: { fontSize: "12.5px", color: "#b42318", marginTop: "10px" },
    mitte: { ...shorthands.padding("22px", "0"), textAlign: "center", color: "#6b5a66", fontSize: "13px" },
    knopf: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.gap("8px"),
        ...shorthands.padding("9px", "14px"),
        ...shorthands.borderRadius("9px"),
        ...shorthands.border("1.5px", "solid", "#ddd3dc"),
        backgroundColor: "#fff",
        color: "#7d2169",
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "12.5px",
        fontWeight: 700,
        lineHeight: 1,
        cursor: "pointer",
        whiteSpace: "nowrap",
        ":hover": { ...shorthands.borderColor("#cd8fbd"), backgroundColor: "#f8eef5" },
    },
    fuss: { display: "flex", ...shorthands.gap("10px"), justifyContent: "flex-end" },
});

export function BranchenvorschlagDialog({
    contactId,
    gegenstand,
    onClose,
    onWaehlen,
}: {
    contactId: number;
    /** Die Tätigkeitsbeschreibung aus der Maske — sie geht der gespeicherten vor. */
    gegenstand?: string | null;
    onClose: () => void;
    /** Übernimmt die Einordnung als Hauptbranche in die Maske. */
    onWaehlen: (treffer: { id: number; pfad: string }) => void;
}) {
    const s = useStyles();
    const [laeuft, setLaeuft] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);
    const [antwort, setAntwort] = useState<BranchenvorschlagAntwort | null>(null);

    const holen = useCallback(async () => {
        setLaeuft(true);
        setFehler(null);
        try {
            setAntwort(await brancheVorschlagen(contactId, gegenstand));
        } catch (e) {
            setFehler(e instanceof Error ? e.message : String(e));
        } finally {
            setLaeuft(false);
        }
    }, [contactId, gegenstand]);

    useEffect(() => {
        void holen();
    }, [holen]);

    return createPortal(
        <div className="kvp-rd-scrim" onClick={onClose}>
            <div className="dm" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className="dm-head">
                    <div className="dm-head-top">
                        <div className="dm-titles">
                            <div className="dm-kicker">Einordnung</div>
                            <h2>Branche aus dem Unternehmensgegenstand</h2>
                        </div>
                        <button className="dm-x" onClick={onClose} aria-label="Schließen">
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round"><path d="M4.5 4.5l9 9M13.5 4.5l-9 9" /></svg>
                        </button>
                    </div>
                </div>

                <div className="dm-body">
                    {laeuft && <div className={s.mitte}>Der Katalog wird durchgesehen …</div>}

                    {!laeuft && antwort && (
                        <>
                            <div className={s.gegenstand}>
                                <span className={s.beschriftung}>Grundlage</span>
                                {antwort.gegenstand}
                            </div>
                            {antwort.vorhanden && (
                                <div className={s.kopfzeile}>
                                    Eingetragen ist derzeit: <strong>{antwort.vorhanden.pfad}</strong>
                                </div>
                            )}

                            {antwort.treffer.map((t) => (
                                <div
                                    key={t.id}
                                    className={s.zeile}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => {
                                        onWaehlen({ id: t.id, pfad: t.pfad });
                                        onClose();
                                    }}
                                    onKeyDown={(ev) => {
                                        if (ev.key === "Enter" || ev.key === " ") {
                                            onWaehlen({ id: t.id, pfad: t.pfad });
                                            onClose();
                                        }
                                    }}
                                >
                                    <span className={s.inhalt}>
                                        <span className={s.pfad}>{t.pfad}</span>
                                        <span className={s.woerter}>
                                            wegen: {t.woerter.join(", ")}
                                        </span>
                                    </span>
                                    <span className={s.punkte}>{t.punkte}</span>
                                </div>
                            ))}

                            {antwort.hinweise.map((h, i) => (
                                <div key={i} className={s.hinweis}>{h}</div>
                            ))}
                        </>
                    )}

                    {fehler && <div className={s.fehler}>{fehler}</div>}
                </div>

                <div className={mergeClasses("dm-foot", s.fuss)}>
                    <button type="button" className={s.knopf} onClick={onClose}>
                        Schließen
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
